// Interval-transition eligibility and recurring-plan lifecycle policy.
import { InvoicePlanStatus } from '@wonderschool/common-base-types';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { DISPLAY_INTERVAL, SERVICE_DIRECTION } from '../../contacts/enums';
import { billingIntervalsEqual, TWICE_PER_MONTH } from './cadence';

export type IntervalChangeBlockReason =
  | 'invalidServiceBoundary'
  | 'pendingInvoice'
  | 'planPaused'
  | 'planEnded'
  | 'planNotActive'
  | 'unsupportedServiceDirection';

// Interval edits are permitted only when the next invoice is still unprocessed
// and its service period can begin exactly one calendar day after the last one.
// Anything uncertain fails closed instead of risking an overlap or gap.
export function getIntervalChangeBlockReason({
  invoicePlan,
  nextBillingInterval,
  now = Date.now(),
  timezone = 'utc',
}: {
  invoicePlan: any;
  nextBillingInterval?: string;
  now?: number;
  timezone?: string;
}): IntervalChangeBlockReason | null {
  if (!invoicePlan?.oldBillingInterval || !nextBillingInterval) return null;
  if (billingIntervalsEqual(invoicePlan.oldBillingInterval, nextBillingInterval)) return null;

  if (invoicePlan.intervalChangePreviousBillingInterval) return 'pendingInvoice';
  if (invoicePlan.status === InvoicePlanStatus.PAUSED) return 'planPaused';
  if (invoicePlan.status === InvoicePlanStatus.ENDED) return 'planEnded';
  if (invoicePlan.status !== InvoicePlanStatus.ACTIVE) return 'planNotActive';
  if (!invoicePlan.dateSend || moment(invoicePlan.dateSend).tz(timezone).isSameOrBefore(moment(now).tz(timezone))) {
    return 'pendingInvoice';
  }
  if (!isValidBillingBoundary(invoicePlan.dateDue)) return 'invalidServiceBoundary';
  if (!invoicePlan.isServiceDatesEnabled) return null;
  if (String(invoicePlan.serviceDirection).toLowerCase() !== SERVICE_DIRECTION.Next) {
    return 'unsupportedServiceDirection';
  }
  if (!isValidBillingBoundary(invoicePlan.dateServiceEnd)) return 'invalidServiceBoundary';
  // A persisted period whose start is after its end is not a state the scheduler
  // can produce, so neither field can be trusted as the last generated boundary.
  if (
    isValidBillingBoundary(invoicePlan.dateServiceStart) &&
    moment(invoicePlan.dateServiceStart)
      .tz(timezone)
      .startOf('day')
      .isAfter(moment(invoicePlan.dateServiceEnd).tz(timezone).startOf('day'))
  ) {
    return 'invalidServiceBoundary';
  }

  // dateServiceEnd is the persisted boundary of the last invoice that was
  // actually generated. dateServiceStart describes that same, already billed
  // period and must never be treated as the future boundary.
  // The retained due date and prior service boundary are independent persisted
  // scheduler values. The backend derives the new start from dateServiceEnd;
  // the editor must not manufacture a relationship between them.
  return null;
}

function isValidBillingBoundary(value: unknown) {
  if (typeof value === 'number' && !Number.isFinite(value)) return false;
  return value != null && moment(value as any).isValid();
}

export function isCadenceTransitionConfirmation(invoicePlan: any) {
  if (!invoicePlan) return false;
  if (invoicePlan.intervalTransitionRecovery) return true;
  if (['awaiting_first_invoice', 'first_invoice_committed'].includes(invoicePlan.intervalTransitionPhase)) return true;

  return Boolean(
    invoicePlan.oldBillingInterval &&
      !billingIntervalsEqual(invoicePlan.oldBillingInterval, invoicePlan.billingInterval)
  );
}

export const isRecurringPlanEligibleForArchive = (plan) => {
  if (plan.isArchived) return false;
  return [InvoicePlanStatus.ENDED, InvoicePlanStatus.PAUSED].includes(plan.status);
};

/**
 * The single status rule for a plan with a (possibly scheduled) end date.
 *
 * When `options.now` is provided (the end-plan flow), the rule is lifecycle-aware:
 * - a passed `dateEnd` means ENDED;
 * - a future `dateEnd` preserves the plan's current status (ACTIVE stays ACTIVE, PAUSED
 *   stays PAUSED) and the daily lifecycle job performs the transition at the cutoff —
 *   even when `dateEnd` falls before the next `dateDue` (CCMS-8116 AC 2/4/5).
 *
 * Without `options`, the legacy dateDue comparison is preserved for the existing
 * create/edit save paths (StepConfirmAndSave, AddToBilling).
 */
export const getInvoicePlanStatus = (dateDue, dateEnd, options?: { now?: number; currentStatus?: string }) => {
  if (!dateEnd) return InvoicePlanStatus.ACTIVE;
  if (options?.now != null) {
    // SCHEDULED is an in-flight handoff to the invoice-plan trigger. Preserve it
    // even when the provider chooses an immediate cutoff so that the worker stays
    // authoritative for SEND versus ENDED.
    if (options.currentStatus === InvoicePlanStatus.SCHEDULED) return InvoicePlanStatus.SCHEDULED;
    if (dateEnd <= options.now) return InvoicePlanStatus.ENDED;
    return options.currentStatus === InvoicePlanStatus.PAUSED ? InvoicePlanStatus.PAUSED : InvoicePlanStatus.ACTIVE;
  }
  return moment(dateEnd).isAfter(dateDue) ? InvoicePlanStatus.ACTIVE : InvoicePlanStatus.ENDED;
};

export function getDisplayInterval(billingInterval) {
  // Compare case-insensitively: the stored billingInterval is title-cased
  // ('Monthly', 'Twice Per Month', 'Twice per month', ...) depending on the entry
  // path, so match the lowercased value rather than a fixed-case literal.
  switch (billingInterval?.toLowerCase()) {
    case 'monthly':
      return DISPLAY_INTERVAL.Month;
    case 'bi-weekly':
      return DISPLAY_INTERVAL.Biweekly;
    case 'weekly':
      return DISPLAY_INTERVAL.Weekly;
    case TWICE_PER_MONTH:
      return DISPLAY_INTERVAL.TwicePerMonth;
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Tuition autofill: pre-fill a recurring plan's charges from the organization's
// tuition settings, converting the stored monthly rate into a per-charge amount.
// ---------------------------------------------------------------------------

// Tuition is configured per month, so every other cadence divides that monthly
// rate: 4.33 is the average weeks per month (52 / 12), bi-weekly is half that
// cadence, and twice-per-month is exactly two charges per calendar month.
